import { formatUsDate, formatUsDateTime } from "../../utils/timezone";

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function money(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function formatSelectedOptions(options = {}) {
  return Object.entries(options || {})
    .filter(([, value]) => value)
    .map(([name, value]) => `${name}: ${value}`)
    .join(" / ");
}

function addressLines(address = {}) {
  return [
    address.fullName,
    address.line1,
    address.line2,
    [address.city, address.state, address.postalCode].filter(Boolean).join(", "),
    address.country,
  ].filter(Boolean);
}

export function printPackingSlip(order) {
  if (!order) return;

  const slipWindow = window.open("", "_blank", "width=900,height=1100");
  if (!slipWindow) return;

  const items = order.items || [];
  const shippingAddress = addressLines(order.shippingAddress);
  const orderDate = formatUsDate(order.createdAt || new Date());
  const itemRows = items
    .map((item) => `
      <tr>
        <td>
          <strong>${escapeHtml(item.name)}</strong>
          <div class="muted">SKU: ${escapeHtml(item.sku || "N/A")}</div>
          ${formatSelectedOptions(item.selectedOptions) ? `<div class="muted">${escapeHtml(formatSelectedOptions(item.selectedOptions))}</div>` : ""}
        </td>
        <td class="center">${escapeHtml(item.quantity)}</td>
        <td class="right">${money(item.price)}</td>
        <td class="right">${money(Number(item.price || 0) * Number(item.quantity || 0))}</td>
      </tr>
    `)
    .join("");

  slipWindow.document.write(`
    <!doctype html>
    <html>
      <head>
        <title>Packing Slip ${escapeHtml(order.orderNumber)}</title>
        <style>
          * { box-sizing: border-box; }
          body {
            margin: 0;
            padding: 32px;
            color: #111827;
            font-family: Arial, Helvetica, sans-serif;
            background: #f3f4f6;
          }
          .page {
            max-width: 820px;
            margin: 0 auto;
            background: #fff;
            padding: 38px;
            border: 1px solid #e5e7eb;
          }
          .top {
            display: flex;
            justify-content: space-between;
            gap: 24px;
            border-bottom: 3px solid #4391d1;
            padding-bottom: 22px;
          }
          .brand {
            font-size: 28px;
            font-weight: 900;
            letter-spacing: .03em;
            text-transform: uppercase;
          }
          .muted { color: #6b7280; font-size: 12px; line-height: 1.55; }
          .title {
            text-align: right;
            font-size: 24px;
            font-weight: 900;
            text-transform: uppercase;
          }
          .meta {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 14px;
            margin: 24px 0;
          }
          .box {
            border: 1px solid #e5e7eb;
            border-radius: 10px;
            padding: 14px;
          }
          .label {
            color: #6b7280;
            font-size: 10px;
            font-weight: 900;
            letter-spacing: .08em;
            text-transform: uppercase;
            margin-bottom: 7px;
          }
          .value { font-size: 14px; font-weight: 800; }
          .two {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 18px;
            margin-bottom: 24px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }
          th {
            background: #f8fafc;
            color: #64748b;
            font-size: 11px;
            letter-spacing: .07em;
            text-transform: uppercase;
            text-align: left;
            padding: 12px;
            border-bottom: 1px solid #e5e7eb;
          }
          td {
            padding: 14px 12px;
            border-bottom: 1px solid #eef2f7;
            font-size: 13px;
            vertical-align: top;
          }
          .center { text-align: center; }
          .right { text-align: right; }
          .totals {
            width: 280px;
            margin-left: auto;
            margin-top: 18px;
          }
          .totals div {
            display: flex;
            justify-content: space-between;
            padding: 7px 0;
            font-size: 13px;
          }
          .totals .grand {
            border-top: 2px solid #111827;
            margin-top: 6px;
            padding-top: 10px;
            font-size: 16px;
            font-weight: 900;
          }
          .footer {
            margin-top: 34px;
            border-top: 1px solid #e5e7eb;
            padding-top: 18px;
            display: flex;
            justify-content: space-between;
            gap: 24px;
            font-size: 12px;
            color: #6b7280;
          }
          .barcode {
            border: 1px dashed #9ca3af;
            padding: 10px 18px;
            font-family: "Courier New", monospace;
            color: #111827;
            font-weight: 900;
            letter-spacing: .12em;
          }
          @media print {
            body { background: #fff; padding: 0; }
            .page { border: 0; max-width: none; min-height: 100vh; }
          }
        </style>
      </head>
      <body>
        <main class="page">
          <section class="top">
            <div>
              <div class="brand">Bobbin USA</div>
              <div class="muted">
                Sewing machine parts, bobbins, needles, and supplies<br />
                bobbinusa@gmail.com · 213-700-2967
              </div>
            </div>
            <div>
              <div class="title">Packing Slip</div>
              <div class="muted">Generated ${escapeHtml(formatUsDateTime(new Date()))}</div>
            </div>
          </section>

          <section class="meta">
            <div class="box">
              <div class="label">Order</div>
              <div class="value">${escapeHtml(order.orderNumber)}</div>
            </div>
            <div class="box">
              <div class="label">Order Date</div>
              <div class="value">${escapeHtml(orderDate)}</div>
            </div>
            <div class="box">
              <div class="label">Channel</div>
              <div class="value">${escapeHtml(order.channel || "direct")}</div>
            </div>
          </section>

          <section class="two">
            <div class="box">
              <div class="label">Ship To</div>
              <div class="value">${shippingAddress.map(escapeHtml).join("<br />")}</div>
            </div>
            <div class="box">
              <div class="label">Customer</div>
              <div class="value">${escapeHtml(order.user?.name || "Customer")}</div>
              <div class="muted">${escapeHtml(order.user?.email || "")}</div>
              <div class="muted">Payment: ${escapeHtml(order.paymentStatus || "")}</div>
            </div>
          </section>

          <section>
            <div class="label">Items To Pack</div>
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th class="center">Qty</th>
                  <th class="right">Unit</th>
                  <th class="right">Line Total</th>
                </tr>
              </thead>
              <tbody>${itemRows}</tbody>
            </table>
            <div class="totals">
              <div><span>Subtotal</span><strong>${money(order.subtotal)}</strong></div>
              <div><span>Shipping</span><strong>${money(order.shipping)}</strong></div>
              <div><span>Tax</span><strong>${money(order.tax)}</strong></div>
              <div class="grand"><span>Total</span><span>${money(order.total)}</span></div>
            </div>
          </section>

          <section class="footer">
            <div>
              Verify SKU and quantity before sealing package.<br />
              Tracking: ${escapeHtml(order.trackingNumber || "Not assigned")}
            </div>
            <div class="barcode">${escapeHtml(order.orderNumber)}</div>
          </section>
        </main>
        <script>
          window.onload = function () {
            window.focus();
            window.print();
          };
        </script>
      </body>
    </html>
  `);
  slipWindow.document.close();
}
